import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert,
  Typography, Box, CircularProgress, TextField, Divider,
} from "@mui/material";
import { insuranceApi, updatePolicy } from "services";

/** Cancellation: preview (eligibility + refund) then confirm. */
export function CancelDialog({ open, policy, onClose, onDone }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!open || !policy?.orderId) return;
    setError(""); setPreview(null); setLoading(true);
    insuranceApi.getCancellationPreview(policy.orderId)
      .then((r) => setPreview(r?.preview || r))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [open, policy]);

  const confirm = async () => {
    setError(""); setLoading(true);
    try {
      const res = await insuranceApi.confirmCancellation(policy.orderId);
      if (policy.id) await updatePolicy(policy.id, { status: res.status || "cancelled" });
      onDone?.();
      onClose();
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Cancel policy</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading && !preview && <Box sx={{ textAlign: "center", py: 3 }}><CircularProgress size={24} /></Box>}
        {preview && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Cancellation preview for order <b>{policy.orderId}</b>:
            </Typography>
            <Box component="pre" sx={{ bgcolor: "#f9fafb", p: 1.5, borderRadius: 2, fontSize: 12, overflowX: "auto" }}>
              {JSON.stringify(preview, null, 2)}
            </Box>
            <Alert severity="warning" sx={{ mt: 2 }}>Confirming will set the order status to cancelled.</Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">Keep policy</Button>
        <Button variant="contained" color="error" disabled={loading || !preview} onClick={confirm}
          startIcon={loading && <CircularProgress size={16} color="inherit" />}>Confirm cancellation</Button>
      </DialogActions>
    </Dialog>
  );
}

/** Modification: load allowed fields → edit → preview → update. */
export function ModifyDialog({ open, policy, onClose, onDone }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fields, setFields] = useState([]);
  const [changes, setChanges] = useState({});
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!open || !policy?.orderId) return;
    setError(""); setPreview(null); setChanges({}); setLoading(true);
    insuranceApi.getModifiableFields(policy.orderId)
      .then((r) => {
        const list = r?.fields?.fields || r?.fields || r || [];
        setFields(Array.isArray(list) ? list : Object.keys(list || {}));
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [open, policy]);

  const doPreview = async () => {
    setError(""); setLoading(true);
    try { setPreview((await insuranceApi.previewModification(policy.orderId, changes))?.preview || null); }
    catch (e) { setError(e.message); } finally { setLoading(false); }
  };

  const doUpdate = async () => {
    setError(""); setLoading(true);
    try {
      await insuranceApi.updateModification(policy.orderId, changes);
      onDone?.();
      onClose();
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };

  const fieldNames = fields.map((f) => (typeof f === "string" ? f : f.name || f.field)).filter(Boolean);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Modify policy</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading && fieldNames.length === 0 && <Box sx={{ textAlign: "center", py: 3 }}><CircularProgress size={24} /></Box>}
        {fieldNames.length > 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="body2" color="text.secondary">Editable fields for order <b>{policy.orderId}</b>:</Typography>
            {fieldNames.map((name) => (
              <TextField key={name} size="small" label={name} value={changes[name] ?? ""}
                onChange={(e) => setChanges({ ...changes, [name]: e.target.value })} />
            ))}
          </Box>
        )}
        {preview && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Preview</Typography>
            <Box component="pre" sx={{ bgcolor: "#f9fafb", p: 1.5, borderRadius: 2, fontSize: 12, overflowX: "auto" }}>
              {JSON.stringify(preview, null, 2)}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button onClick={doPreview} disabled={loading || Object.keys(changes).length === 0}>Preview changes</Button>
        <Button variant="contained" onClick={doUpdate} disabled={loading || Object.keys(changes).length === 0}
          startIcon={loading && <CircularProgress size={16} color="inherit" />}>Apply update</Button>
      </DialogActions>
    </Dialog>
  );
}
